// Code samples for developers integrating with Openline: the getting-started
// steps against the public API (/api/v1) and webhook signature verification.
// Shown by the server's API reference (/developers) and the dashboard's
// Admin → Developers page, so both teach the same code. The server tests run
// the Node verifier against the real signer (server/src/openapi/).

export type SnippetLang = 'curl' | 'node' | 'python';

export const SNIPPET_LANGS: ReadonlyArray<{ id: SnippetLang; label: string }> = [
  { id: 'curl', label: 'curl' },
  { id: 'node', label: 'Node.js' },
  { id: 'python', label: 'Python' },
];

export interface QuickstartStep {
  id: string;
  title: string;
  /** Plain text; `code` spans are allowed. */
  body: string;
  code: Record<SnippetLang, string>;
}

/** The getting-started steps, with `origin` (the Openline server) filled in. */
export function quickstartSteps(origin: string): QuickstartStep[] {
  const api = `${origin.replace(/\/+$/, '')}/api/v1`;
  return [
    {
      id: 'check-key',
      title: 'Check your key',
      body:
        'Create a key in Admin → API keys and keep it on your server. Every request sends it as a bearer token. ' +
        '`/me` works with any key and lists the sites it can reach.',
      code: {
        curl: `export OPENLINE_KEY="olk_…"\n\ncurl ${api}/me \\\n  -H "Authorization: Bearer $OPENLINE_KEY"`,
        node: `const API = '${api}';\nconst headers = { Authorization: \`Bearer \${process.env.OPENLINE_KEY}\` };\n\nconst me = await fetch(\`\${API}/me\`, { headers }).then((r) => r.json());\nconsole.log(me.key.scopes, me.sites);`,
        python: `import os, requests\n\nAPI = "${api}"\nheaders = {"Authorization": f"Bearer {os.environ['OPENLINE_KEY']}"}\n\nme = requests.get(f"{API}/me", headers=headers).json()\nprint(me["key"]["scopes"], me["sites"])`,
      },
    },
    {
      id: 'list-conversations',
      title: 'List open conversations',
      body:
        'Needs `conversations:read`. Newest first; pass `next_cursor` back as `cursor` for the next page. ' +
        'Filter with `status`, `channel` and `site_id`.',
      code: {
        curl: `curl "${api}/conversations?status=open&limit=10" \\\n  -H "Authorization: Bearer $OPENLINE_KEY"`,
        node: `const { data, next_cursor } = await fetch(\`\${API}/conversations?status=open&limit=10\`, { headers }).then((r) => r.json());\nfor (const c of data) console.log(c.id, c.visitor.name, c.owner);`,
        python: `page = requests.get(f"{API}/conversations", headers=headers, params={"status": "open", "limit": 10}).json()\nfor c in page["data"]:\n    print(c["id"], c["visitor"]["name"], c["owner"])`,
      },
    },
    {
      id: 'read-messages',
      title: 'Read a conversation’s messages',
      body: 'Oldest first. `author` is `visitor`, `agent`, `ai` or `system`.',
      code: {
        curl: `curl "${api}/conversations/$CONVERSATION_ID/messages" \\\n  -H "Authorization: Bearer $OPENLINE_KEY"`,
        node: `const messages = await fetch(\`\${API}/conversations/\${conversationId}/messages\`, { headers }).then((r) => r.json());\nfor (const m of messages.data) console.log(m.seq, m.author, m.text);`,
        python: `messages = requests.get(f"{API}/conversations/{conversation_id}/messages", headers=headers).json()\nfor m in messages["data"]:\n    print(m["seq"], m["author"], m["text"])`,
      },
    },
    {
      id: 'send-reply',
      title: 'Send a reply',
      body:
        'Needs `conversations:write`. The visitor sees it live in the widget (or by email on an email conversation). ' +
        'An `Idempotency-Key` makes retries safe: the same key never sends twice.',
      code: {
        curl: `curl -X POST "${api}/conversations/$CONVERSATION_ID/messages" \\\n  -H "Authorization: Bearer $OPENLINE_KEY" \\\n  -H "Content-Type: application/json" \\\n  -H "Idempotency-Key: order-1042-shipped" \\\n  -d '{"text": "Your order shipped today.", "author_name": "Order Bot"}'`,
        node: `const res = await fetch(\`\${API}/conversations/\${conversationId}/messages\`, {\n  method: 'POST',\n  headers: { ...headers, 'Content-Type': 'application/json', 'Idempotency-Key': 'order-1042-shipped' },\n  body: JSON.stringify({ text: 'Your order shipped today.', author_name: 'Order Bot' }),\n});\nif (!res.ok) throw new Error((await res.json()).message);`,
        python: `res = requests.post(\n    f"{API}/conversations/{conversation_id}/messages",\n    headers={**headers, "Idempotency-Key": "order-1042-shipped"},\n    json={"text": "Your order shipped today.", "author_name": "Order Bot"},\n)\nres.raise_for_status()`,
      },
    },
  ];
}

/** Verifying `X-Openline-Signature` on a webhook receiver. No `curl` sample. */
export const WEBHOOK_VERIFY: Record<Exclude<SnippetLang, 'curl'>, string> = {
  node: `const crypto = require('node:crypto');

// rawBody: the request body exactly as received (a string or Buffer),
// not re-serialised JSON. With Express: express.raw({ type: 'application/json' }).
function verifyOpenline(secret, header, rawBody) {
  const parts = Object.fromEntries((header || '').split(',').map((kv) => kv.split('=')));
  if (!parts.t || Math.abs(Date.now() / 1000 - Number(parts.t)) > 300) return false;
  const expected = crypto.createHmac('sha256', secret).update(parts.t + '.' + rawBody).digest('hex');
  const got = Buffer.from(parts.v1 || '', 'hex');
  return got.length === 32 && crypto.timingSafeEqual(got, Buffer.from(expected, 'hex'));
}`,
  python: `import hashlib, hmac, time

# raw_body: the request body bytes exactly as received (Flask: request.get_data()).
def verify_openline(secret: str, header: str, raw_body: bytes) -> bool:
    parts = dict(p.split("=", 1) for p in (header or "").split(",") if "=" in p)
    t, v1 = parts.get("t"), parts.get("v1", "")
    if not t or not t.isdigit() or abs(time.time() - int(t)) > 300:
        return False
    expected = hmac.new(secret.encode(), t.encode() + b"." + raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, v1)`,
};
